import { defaultV3, v3WithImageBg } from '@consumer/logic/v3'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import { cloneDeep, deepClean } from '@shared/object'
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string'
import type { MaybeRefOrGetter } from 'vue'

export function encodeGift (gift: GiftTemplate) {
  return compressToEncodedURIComponent(JSON.stringify(gift))
}

export function decodeGift (encodedGift: string) {
  return JSON.parse(decompressFromEncodedURIComponent(encodedGift)) as GiftTemplate
}

export const useGiftTemplate = (giftRef: MaybeRefOrGetter<GiftTemplate>) => {
  const giftTemplate = computed<GiftTemplate>(() => {
    let {
      amount,
      giftlyMetadata,
      giftlyVersion = 3,
      places,
    } = toValue(giftRef)

    // NOTE: Avoid including the blurb, it can be reallly long.
    giftlyMetadata = cloneDeep(giftlyMetadata || {})
    if (giftlyMetadata?.backgroundImageUrl) {
      giftlyMetadata.designName ||= v3WithImageBg
    }
    delete giftlyMetadata?.giftGuideBlurb

    // NOTE: Avoid overflowing the URL size, we don't need to include all info for places.
    places = places?.map(({ giftlyId, name, placeType }) => ({ giftlyId, name, placeType }))

    return deepClean({
      amount,
      giftlyMetadata,
      giftlyVersion,
      places,
    })
  })

  const giftTemplateParam = computed(() => encodeGift(giftTemplate.value))

  const multiPlaceGiftIdeaTemplate = computed(() => {
    const { giftlyMetadata, places } = toValue(giftRef)

    return deepClean(cloneDeep({
      giftly_metadata: {
        design_name: giftlyMetadata?.backgroundImageUrl ? v3WithImageBg : defaultV3.name,
        background_image_url: giftlyMetadata?.backgroundImageUrl,
        background_color: giftlyMetadata?.backgroundColor,
        mask_name: giftlyMetadata?.maskName,
        mask_color: giftlyMetadata?.maskColor,
        logo_color: giftlyMetadata?.logoColor,
        unsplash_image_id: giftlyMetadata?.unsplashImageId,
        tagline: giftlyMetadata?.tagline,
        design_search_terms: giftlyMetadata?.designSearchTerms,
      },
      places: places?.map((p) => {
        if (p.placeType === 'list' || p.placeType === 'brand' || p.placeType === 'yelp') {
          return { url_slug: p.id }
        }
        else {
          return { name: p.name, place_type: p.placeType }
        }
      }),
    }))
  })

  return {
    giftTemplate,
    giftTemplateParam,
    multiPlaceGiftIdeaTemplate,
  }
}
